import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { toast } from '../components/ui/use-toast';

export const useCopy = () => {
  const t = useTranslations();

  const copyWithLegacyApi = (text: string) => {
    const input = document.createElement('textarea');
    input.value = text;
    input.setAttribute('readonly', '');
    input.style.position = 'fixed';
    input.style.opacity = '0';
    document.body.appendChild(input);
    input.select();
    input.setSelectionRange(0, input.value.length);
    const copied = document.execCommand('copy');
    document.body.removeChild(input);
    if (!copied) {
      throw new Error('copyFailed');
    }
  };

  const copy = (
    text: string,
    options?: {
      copySuccessMessage?: string | ReactNode;
      copyFailedMessage?: string | ReactNode;
      hideMessage?: boolean;
    },
  ) => {
    return new Promise((resolve, reject) => {
      const writeText = navigator.clipboard?.writeText(text);
      if (!writeText) {
        copyWithLegacyApi(text);
        !options?.hideMessage &&
          toast({
            type: 'success',
            message: options?.copySuccessMessage ?? t('popup.toast.copied'),
          });
        resolve(true);
        return;
      }
      writeText
        .then(() => {
          !options?.hideMessage &&
            toast({
              type: 'success',
              message: options?.copySuccessMessage ?? t('popup.toast.copied'),
            });
          resolve(true);
        })
        .catch((_error) => {
          try {
            copyWithLegacyApi(text);
            !options?.hideMessage &&
              toast({
                type: 'success',
                message: options?.copySuccessMessage ?? t('popup.toast.copied'),
              });
            resolve(true);
          } catch {
            !options?.hideMessage &&
              toast({
                type: 'error',
                message: options?.copyFailedMessage ?? t('popup.toast.copyFailed'),
              });
            reject(new Error('copyFailed'));
          }
        });
    });
  };

  const copyAsync = async (
    getTextPromise: Promise<string | null>,
    options?: {
      copySuccessMessage?: string | ReactNode;
      copyFailedMessage?: string | ReactNode;
      hideMessage?: boolean;
      placeholder?: string;
    },
  ) => {
    try {
      // 检查是否支持 Clipboard API
      if (!navigator.clipboard) {
        throw new Error('Clipboard API not supported');
      }

      // 方案1: 使用 ClipboardItem (推荐，但需要浏览器支持)
      if ('write' in navigator.clipboard) {
        const clipboardItem = new ClipboardItem({
          'text/plain': getTextPromise.then((text) => {
            if (!text) throw new Error('No text to copy');
            return new Blob([text], { type: 'text/plain' });
          }),
        });

        await navigator.clipboard.write([clipboardItem]);

        !options?.hideMessage &&
          toast({
            type: 'success',
            message: options?.copySuccessMessage ?? t('popup.toast.copied'),
          });
        return true;
      }

      // 方案2: 先写入占位符，再写入真实内容
      if ('writeText' in navigator.clipboard) {
        const placeholder = options?.placeholder || 'Loading...';
        // @ts-expect-error - TypeScript 类型推断问题，实际运行时 clipboard 存在
        await navigator.clipboard.writeText(placeholder);

        const text = await getTextPromise;
        if (!text) {
          throw new Error('No text to copy');
        }
        // @ts-expect-error - TypeScript 类型推断问题，实际运行时 clipboard 存在
        await navigator.clipboard.writeText(text);

        !options?.hideMessage &&
          toast({
            type: 'success',
            message: options?.copySuccessMessage ?? t('popup.toast.copied'),
          });
        return true;
      }

      throw new Error('Clipboard API not supported');
    } catch (error) {
      console.error('Async copy failed:', error);
      !options?.hideMessage &&
        toast({
          type: 'error',
          message: options?.copyFailedMessage ?? t('popup.toast.copyFailed'),
        });
      throw new Error('copyFailed');
    }
  };

  return {
    copy,
    copyAsync,
  };
};
